import React, { useContext, useEffect, useState } from 'react';
import {
	Wrapper,
	StepFormWrapper,
	StepSection,
	Text,
} from './GenerateSpendCube.styles';
import UploadFile from './UploadFile/UploadFile';
import { GenerateSpendCubeContext } from 'contexts/GenerateSpendCubeContext';
import Processing from './ProcessingPage/Processing';
import MappingManager from './MappingManager/MappingManager';
import ErrorLogs from './ErrorLogs/ErrorLogs';
import FileUploadError from './FileUploadError/FileUploadError';
import { getColumnHeaders, getExcludedSummary } from 'utils/apiRequests';
import { ApSignalrConsumer } from '@alixpartners/ui-utils';
import { ETL_PROCESS_STATUS } from 'Data/constants';
import { useToastMessageContext } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { NotificationTypes } from 'components/Toast/constants';
import Modal from 'components/Modal';
import { TextInput } from 'Settings/TextInput';
import ETLProgress from './ETLProgress/ETLProgress';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

export const GenerateSpendCube = () => {
	const {
		currentState,
		getCurrentEtlState,
		setType,
		pageLoading,
		dataForMapping,
		isShowRestartModal,
		setIsShowRestartModal,
		restart,
		setPageLoading,
		isEtl,
	} = useContext(GenerateSpendCubeContext);
	const history = useHistory();
	const { showToast } = useToastMessageContext();
	const [columnData, setColumnData] = useState(dataForMapping);
	const [currentLoadingMessage, setCurrentLoadingMessage] = useState({
		message: 'File upload is in progress',
		text: 'Please stay on the application until all files are uploaded.',
	});
	const [isFileExpired, setIsFileExpired] = useState(false);
	const [logFiles, setlogFiles] = useState([]);
	const [phrase, setPhrase] = useState('');

	const RESTART_PHRASE = 'restart etl process';

	const {
		NO_DATA,
		DATA_UPLOADING,
		DATA_UPLOADED,
		EXTRACTING_HEADERS,
		MAPPING_STARTED,
		UNCLASSIFIED_FILE_GENERATING,
		UNCLASSIFIED_FILE_GENERATED,
		NORMALIZATION_IN_PROGRESS,
		NORMALIZATION_SUCCEEDED,
		CLASSIFICATION_IN_PROGRESS,
		CLASSIFICATION_SUCCEEDED,
		UPLOAD_FAILED,
		COLUMN_EXTRACTION_FAILED,
		UNCLASSIFIED_FILE_GENERATION_FAILED,
	} = ETL_PROCESS_STATUS;

	const getLogFiles = () => {
		setCurrentLoadingMessage({
			message: 'Loading log files.',
		});
		setPageLoading(true);
		getExcludedSummary()
			.then(res => {
				setlogFiles(res?.data);
				getCurrentEtlState();
			})
			.catch(err => {
				console.error(err);
				showToast('Unable to fetch log file.', NotificationTypes.danger, 3000);
				setPageLoading(false);
			});
	};

	useEffect(() => {
		if (
			currentState === MAPPING_STARTED &&
			Object.keys(dataForMapping).length === 0
		) {
			getColumnHeaders()
				.then(res => {
					if (res?.succeeded) {
						setColumnData(res?.data);
						setType(res?.data?.type);
					} else {
						console.log('Error fetching column headers.');
						showToast(
							'Could not fetch column Headers',
							NotificationTypes.danger,
							3000
						);
						setIsFileExpired(true);
					}
				})
				.catch(error => {
					console.error(error);
					showToast(
						'Could not fetch column Headers',
						NotificationTypes.danger,
						3000
					);
					setIsFileExpired(true);
				});
		} else if (currentState === UNCLASSIFIED_FILE_GENERATED) {
			getLogFiles();
		} else if (currentState === CLASSIFICATION_IN_PROGRESS) {
			setTimeout(() => {
				history.push(`/data/append`);
			}, 3000);
		} else if (currentState === NORMALIZATION_IN_PROGRESS) {
			setTimeout(() => {
				history.push(`/data/vendor-cleanup`);
			}, 3000);
		}
		//eslint-disable-next-line
	}, []);

	const fetchSignalR = message => {
		if (message === 'File Reading Failed!!!') {
			getCurrentEtlState();
		} else if (message === 'Reading All Files Successful!!!') {
			getColumnHeaders()
				.then(res => {
					if (res?.succeeded) {
						setColumnData(res?.data);
						setType(res?.data?.type);
						getCurrentEtlState();
					} else {
						console.log('Error fetching column headers.');
						getCurrentEtlState();
					}
				})
				.catch(error => {
					console.error(error);
					showToast(
						'Could not fetch column Headers',
						NotificationTypes.danger,
						3000
					);
				});
		} else if (message === 'Unclassified File Generated successfully') {
			getLogFiles();
			getCurrentEtlState();
		} else if (message === 'Unclassified File Generation Failed!!') {
			getCurrentEtlState();
		}
	};

	const onInputChange = e => {
		setPhrase(e.target.value);
	};

	const confirmRestart = () => {
		setPhrase('');
		restart();
	};

	return (
		<Wrapper>
			<ApSignalrConsumer
				message="ETLFilesRead"
				onMessage={(key, message) => {
					fetchSignalR(message);
				}}
			/>
			<ApSignalrConsumer
				message="UploadCompleted"
				onMessage={() => {
					getCurrentEtlState();
				}}
			/>
			{isShowRestartModal && (
				<Modal
					submitLabel="Yes"
					cancelLabel="No"
					onClose={() => {
						setIsShowRestartModal(false);
					}}
					onSubmit={
						phrase === RESTART_PHRASE.toUpperCase() ? confirmRestart : null
					}
					title="Do you want to restart the process?"
				>
					<Text>
						<p>You are about to restart the ETL process.</p>
						<br />
						<p>This will remove all the current ETL progress.</p>
						<p>
							You'll have to re-upload the files and start the mapping process
							from the beginning.
						</p>
						<br />
						<TextInput
							value={phrase}
							name="deleteCheck"
							label={`Type [${RESTART_PHRASE.toUpperCase()}] in the box below and click OK.`}
							onChange={onInputChange}
						/>
					</Text>
				</Modal>
			)}

			{pageLoading ? (
				<StepSection>
					<StepFormWrapper>
						<Processing
							message={currentLoadingMessage?.message}
							text={currentLoadingMessage?.text}
						/>
					</StepFormWrapper>
				</StepSection>
			) : (
				<StepSection>
					<StepFormWrapper>
						{currentState === NO_DATA && (
							<UploadFile setCurrentLoadingMessage={setCurrentLoadingMessage} />
						)}
						{[DATA_UPLOADING, DATA_UPLOADED].includes(currentState) && (
							<Processing
								message="File upload is in progress"
								text="Please stay on the application until all files are uploaded."
							/>
						)}
						{currentState === EXTRACTING_HEADERS && (
							<Processing
								message="Header extraction is in progress"
								text="File processing and header extraction may take some time. You will be notified once all the files are processed."
							/>
						)}
						{currentState === MAPPING_STARTED && (
							<MappingManager
								filterData={columnData}
								setCurrentLoadingMessage={setCurrentLoadingMessage}
								isFileExpired={isFileExpired}
							/>
						)}
						{currentState === UNCLASSIFIED_FILE_GENERATING && (
							<Processing
								message="Template generation is in progress"
								text="Unclassified spend cube is being generated."
							/>
						)}

						{currentState === UNCLASSIFIED_FILE_GENERATED && (
							<ErrorLogs
								logFiles={logFiles}
								setCurrentLoadingMessage={setCurrentLoadingMessage}
							/>
						)}

						{currentState === NORMALIZATION_IN_PROGRESS && (
							<ETLProgress
								statusMessage="Normalization is in progress. Please check the Vendor Normalization page to view detailed status."
								progressStep={40}
							/>
						)}

						{currentState === NORMALIZATION_SUCCEEDED && (
							<ETLProgress
								statusMessage={
									isEtl === 1
										? 'Normalization completed, Classification is being initiated.'
										: 'Normalization completed.'
								}
								progressStep={60}
							/>
						)}

						{currentState === CLASSIFICATION_IN_PROGRESS && (
							<ETLProgress
								statusMessage="Classification is in progress. Please check the Append New Data page to view detailed status."
								progressStep={80}
							/>
						)}
						{currentState === CLASSIFICATION_SUCCEEDED && (
							<ETLProgress
								statusMessage="Classification completed."
								progressStep={100}
							/>
						)}
						{currentState === UPLOAD_FAILED && (
							<FileUploadError
								errorMessage="Last file upload could not be completed. Please restart the file
								upload process."
								setCurrentLoadingMessage={setCurrentLoadingMessage}
							/>
						)}
						{currentState === COLUMN_EXTRACTION_FAILED && (
							<FileUploadError
								errorMessage="Column extraction failed because in some files, the column headers were not present."
								setCurrentLoadingMessage={setCurrentLoadingMessage}
							/>
						)}
						{currentState === UNCLASSIFIED_FILE_GENERATION_FAILED && (
							<FileUploadError
								errorMessage="Unclassified spend cube generation failed."
								setCurrentLoadingMessage={setCurrentLoadingMessage}
							/>
						)}
					</StepFormWrapper>
				</StepSection>
			)}
		</Wrapper>
	);
};

export default GenerateSpendCube;
